// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore

"use client";

import classNames from "classnames";
import { FC, forwardRef, InputHTMLAttributes, ReactNode } from "react";
import MaskedInput from "react-text-mask";

import typoInputStyles from "@/shared/ui/typography/typography.module.scss";
import ApproveIcon from "@/svgs/approve.svg";
import ErrorIcon from "@/svgs/exclamation_mark.svg";

import { Typography } from "../typography";

import styles from "./input.module.scss";
import { InputProps } from "./input.types";

const defineElement: Record<NonNullable<InputProps["variant"]>, ReactNode> = {
  error: <ErrorIcon className={styles.errorContent} />,
  success: <ApproveIcon className={styles.successContent} />,
};

const Input: FC<InputProps> = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      required = false,
      disabled = false,
      theme = "dark",
      variant,
      label,
      message,
      hint,
      startContent,
      endContent,
      mask,
      className,
      withDarkBorder,
      onValueChange,
      onChange,
      ...restProps
    },
    ref
  ) => {
    const inputProps: InputHTMLAttributes<HTMLInputElement> = {
      disabled: disabled,
      className: classNames(
        typoInputStyles.subheadline,
        styles.innerInput,
        theme && styles[`--${theme}`],
        variant && styles[variant],
        disabled && styles.disabled
      ),
      onChange: (event) => {
        if (onChange) {
          onChange(event);
        }
        if (onValueChange) {
          onValueChange(event.target.value);
        }
      },
    };

    return (
      <div className={classNames(styles.inputContainer, className)}>
        {label && (
          <Typography variant="footnote">
            {label}
            {required && "*"}
          </Typography>
        )}

        <div
          className={classNames(
            styles.inputContainerWithIcons,
            variant && styles[`${variant}InputContainer`],
            theme && styles[`--${theme}`],
            theme === "dark" && withDarkBorder && styles.darkBorder,
            disabled && styles.disabled
          )}
        >
          {startContent && <div className={styles.startContent}>{startContent}</div>}

          {mask ? (
            <MaskedInput {...restProps} {...inputProps} mask={mask} guide={false} />
          ) : (
            <input {...restProps} {...inputProps} ref={ref} />
          )}

          <div className={styles.endContent}>{(variant && defineElement[variant]) || endContent}</div>
        </div>

        {(message || hint) && (
          <div className={styles.footer}>
            {message && (
              <Typography variant="caption-2" className={classNames(styles[`${variant}Content`])}>
                {message}
              </Typography>
            )}
            {hint}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
